import { useTranslation } from 'react-i18next';
import { TierCard } from './TierCard';

export type PlanType = 'FREE_TRIAL' | 'STARTER' | 'PRO' | 'POWER';

interface TiersAndPricingProps {
  currentPlan?: string;
  onPlanSelect?: (planType: PlanType) => void;
  onManageSubscription?: () => void;
  hideButtons?: boolean;
  isLoading?: boolean;
  trialExpired?: boolean;
  showChoosePlanButtons?: boolean; // Override to show "Choose This Plan" for all tiers
  hideCurrentPlanHighlight?: boolean; // Hide the blue border for current plan
  selectedPlan?: PlanType | null; // Track which plan is selected in the modal
  hasActiveSubscription?: boolean; // Trial users with billing setup can change plans
  scheduledPlanChange?: {
    to: string;
    effectiveDate: string;
    toDisplayName: string;
  } | null;
}

export function TiersAndPricing({
  currentPlan,
  onPlanSelect,
  onManageSubscription,
  hideButtons = false,
  isLoading = false,
  trialExpired = false,
  showChoosePlanButtons = false,
  hideCurrentPlanHighlight = false,
  selectedPlan = null,
  hasActiveSubscription = false,
  scheduledPlanChange = null,
}: TiersAndPricingProps) {
  const { t } = useTranslation();

  // Create tier configurations
  const tiers = [
    {
      tier: 'STARTER' as PlanType,
      title: t('subscription.dialog.plans.lite.title'),
      price: t('subscription.dialog.plans.lite.price'),
      period: t('subscription.dialog.plans.lite.period'),
      features: [
        'Unlimited job applications',
        'Email reminders',
        'Basic analytics dashboard',
        'Email-to-Track (application + resume updates)',
        'Suggested Job feed (1 target country)',
        '5 Application scores / month',
        '5 Follow-up message suggestions / month',
        '5 Job-specific resume reviews / month',
        '5 Auto-generated cover letters per month',
        '5 Tailored resume versions / month',
        '5 Role-Specific Interview Prep Sessions / month (coming soon)',
      ],
    },
    {
      tier: 'PRO' as PlanType,
      title: t('subscription.dialog.plans.pro.title'),
      price: t('subscription.dialog.plans.pro.price'),
      period: t('subscription.dialog.plans.pro.period'),
      features: [
        'Everything in Lite',
        'Suggested Job feed (2 target countries)',
        'Calendar-integrated reminders',
        'Advanced insights dashboard',
        'Resume-ranked Suggested Jobs with match scores',
        '10 Application scores / month',
        '10 Job-specific resume reviews / month',
        '10 Personalized cover letters per month',
        '10 Assisted follow-up message suggestions / month',
        '10 Tailored resume versions / month',
        '10 Role-Specific Interview Prep Sessions / month (coming soon)',
      ],
      badge: t('subscription.dialog.plans.pro.badge'),
      isPermanentHighlight: true,
    },
    {
      tier: 'POWER' as PlanType,
      title: t('subscription.dialog.plans.power.title'),
      price: t('subscription.dialog.plans.power.price'),
      period: t('subscription.dialog.plans.power.period'),
      features: [
        'Everything in Pro',
        'Suggested Job feed (unlimited target countries)',
        'Unlimited resume/position scores',
        'Unlimited Application scores',
        'Unlimited Job-specific resume reviews',
        'Unlimited Personalized cover letters',
        'Unlimited Follow-up message suggestions',
        'Unlimited Tailored resume versions',
        'Unlimited Role-Specific Interview Prep Sessions (coming soon)',
      ],
    },
  ];

  return (
    <div className='grid grid-cols-1 md:grid-cols-3 gap-8 py-1'>
      {tiers.map((tierConfig) => (
        <TierCard
          key={tierConfig.tier}
          tier={tierConfig.tier}
          title={tierConfig.title}
          price={tierConfig.price}
          period={tierConfig.period}
          features={tierConfig.features}
          badge={tierConfig.badge}
          isPermanentHighlight={tierConfig.isPermanentHighlight}
          currentPlan={currentPlan}
          selectedPlan={selectedPlan}
          hideCurrentPlanHighlight={hideCurrentPlanHighlight}
          showChoosePlanButtons={showChoosePlanButtons}
          hasActiveSubscription={hasActiveSubscription}
          trialExpired={trialExpired}
          isLoading={isLoading}
          hideButtons={hideButtons}
          scheduledPlanChange={scheduledPlanChange}
          onPlanSelect={onPlanSelect}
          onManageSubscription={onManageSubscription}
        />
      ))}
    </div>
  );
}
